import { useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Link } from '@mui/material';
import { config } from '~/config';
import { useDispatch, useSelector } from 'react-redux';

import FlightItineraryTripSearchStep from './FlightItineraryTripSearchStep';
import FlightItineraryPassengerDetailsStep from './FlightItineraryPassengerDetailsStep';
import FlightItineraryReviewStep from './FlightItineraryReviewStep';
import ItineraryFlightSelection from './ItineraryFlightSelection';
import { Offer } from '~/types';
import { setBookingDetails } from '~/store/slices/itinerary-wizard';
import { FlightSearchFormValidationMessages, FlightSearchParams, RootState } from '~/types';
import { Currency } from '~/types/common';
import { ItineraryPackage, ItineraryTripDetailsBooking, PassengerInfo } from '~/types';
import {
  getCustomerEmailValidationMessages,
  getPackageCapacityValidationMessage,
  getPassengerValidationMessages,
  getSearchValidationMessages,
  hasPassengerValidationErrors,
  PackageCapacityValidationMessage,
  PassengerValidationMessages,
} from '~/utils/flight-itinerary-validation';
import { trackButtonClick, trackLinkClick } from '~/utils/analytics';

export type ItineraryDetailsStep = 'search' | 'flights' | 'passengers' | 'review';

type Props = {
  currentStep: ItineraryDetailsStep;
  selectedPackage?: Pick<ItineraryPackage, 'max_travelers' | 'title' | 'requiresFlightSelection' | 'dateConstraints'> | null;
  onStepChange: (step: ItineraryDetailsStep) => void;
  onPaymentRequested?: () => void;
};

const restoreDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value as string | number);

  return Number.isNaN(date.getTime()) ? null : date;
};

const restoreSearchParams = (searchParams: FlightSearchParams): FlightSearchParams => ({
  ...searchParams,
  departureDate: restoreDate(searchParams.departureDate),
  returnDate: restoreDate(searchParams.returnDate),
});

function FlightItineraryTripDetails({ currentStep, selectedPackage, onPaymentRequested, onStepChange }: Props) {
  const dispatch = useDispatch();
  const currency = useSelector<RootState, Currency>(({ app }) => app.currency);
  const wizardBookingDetails = useSelector<RootState, Record<string, any> | null>(({ itineraryWizard }) => itineraryWizard?.bookingDetails || null);

  const stored = wizardBookingDetails as ItineraryTripDetailsBooking | null;
  const [selectedFlight, setSelectedFlight] = useState<Offer | undefined>(stored?.selectedFlight);

  const [validationMode, setValidationMode] = useState(false);
  const [searchParams, setSearchParams] = useState<FlightSearchParams>(
    stored?.searchParams ? restoreSearchParams(stored.searchParams) : {
      from: null,
      to: null,
      type: 'return',
      travelClass: 'ECONOMY',
      passengers: { adults: 1, children: 0, infants: 0 },
      departureDate: null,
      returnDate: null,
      currencyCode: currency,
    },
  );
  const [validationMessages, setValidationMessages] = useState<FlightSearchFormValidationMessages>({ to: [], from: [], departure: [], return: [] });
  const [capacityValidationMessage, setCapacityValidationMessage] = useState<PackageCapacityValidationMessage>(null);
  const [customerEmail, setCustomerEmail] = useState(stored?.customerEmail ?? '');
  const [customerEmailValidationMessages, setCustomerEmailValidationMessages] = useState<string[]>([]);
  const [passengers, setPassengers] = useState<PassengerInfo[]>(stored?.passengers ?? []);
  const [passengerValidationMessages, setPassengerValidationMessages] = useState<PassengerValidationMessages>({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsValidationMessage, setShowTermsValidationMessage] = useState(false);

  const generatedPassengers = useMemo(() => {
    const all: PassengerInfo[] = [];
    for (let i = 0; i < searchParams.passengers.adults; i += 1) all.push({ id: `${all.length + 1}`, passengerType: 'Adult', firstName: '', lastName: '', gender: '', dateOfBirth: null });
    for (let i = 0; i < searchParams.passengers.children; i += 1) all.push({ id: `${all.length + 1}`, passengerType: 'Child', firstName: '', lastName: '', gender: '', dateOfBirth: null });
    for (let i = 0; i < searchParams.passengers.infants; i += 1) all.push({ id: `${all.length + 1}`, passengerType: 'Infant', firstName: '', lastName: '', gender: '', dateOfBirth: null });
    return all;
  }, [searchParams.passengers]);

  const persistBookingDetails = (nextSearchParams: FlightSearchParams, nextPassengers: PassengerInfo[], nextCustomerEmail = customerEmail) => {
    dispatch(setBookingDetails({ searchParams: nextSearchParams, passengers: nextPassengers, customerEmail: nextCustomerEmail,
      selectedFlight, search_id: selectedFlight?.searchId, offer_id: selectedFlight?.offerId }));
  };

  const handleSearchNext = () => {
    trackButtonClick('FlightItineraryTripDetails', 'Next: Passenger Details');
    const messages = getSearchValidationMessages(searchParams, selectedPackage?.dateConstraints);
    const capacityMessage = getPackageCapacityValidationMessage(searchParams, selectedPackage);
    setValidationMessages(messages);
    setCapacityValidationMessage(capacityMessage);
    if (messages.to.length || messages.from.length || messages.departure.length || (searchParams.type === 'return' && messages.return.length)) {
      setValidationMode(true);
      return false;
    }
    if (capacityMessage) {
      setValidationMode(true);
      return false;
    }
    setValidationMode(false);
    setCapacityValidationMessage(null);
    setPassengers(generatedPassengers);
    persistBookingDetails(searchParams, generatedPassengers);
    onStepChange(selectedPackage?.requiresFlightSelection ? 'flights' : 'passengers');
    return true;
  };

  const handlePassengerChange = (passengerId: string, field: keyof PassengerInfo, value: string | Date | null) => {
    const next = passengers.map((p) => (p.id === passengerId ? { ...p, [field]: value } : p));
    setPassengers(next);
    setPassengerValidationMessages(getPassengerValidationMessages(next));
    persistBookingDetails(searchParams, next);
  };

  const handleCustomerEmailChange = (value: string) => {
    setCustomerEmail(value);
    setCustomerEmailValidationMessages(getCustomerEmailValidationMessages(value));
    persistBookingDetails(searchParams, passengers, value);
  };

  const validatePassengerDetails = () => {
    const nextPassengerValidationMessages = getPassengerValidationMessages(passengers);
    const nextCustomerEmailValidationMessages = getCustomerEmailValidationMessages(customerEmail);
    setPassengerValidationMessages(nextPassengerValidationMessages);
    setCustomerEmailValidationMessages(nextCustomerEmailValidationMessages);
    return !hasPassengerValidationErrors(nextPassengerValidationMessages) && nextCustomerEmailValidationMessages.length === 0;
  };

  const handlePassengerDetailsNext = () => {
    trackButtonClick('FlightItineraryTripDetails', 'Next: Review details');
    if (!validatePassengerDetails()) return;
    onStepChange('review');
  };

  const handleReviewNext = () => {
    if (selectedPackage?.requiresFlightSelection && !selectedFlight) { onStepChange('flights'); return; }
    trackButtonClick('FlightItineraryTripDetails', 'Next: Pay');
    if (!validatePassengerDetails()) return;
    if (!termsAccepted) {
      setShowTermsValidationMessage(true);
      return;
    }
    onPaymentRequested?.();
  };

  return (
    <Box>
      {currentStep === 'flights' ? <ItineraryFlightSelection searchParams={searchParams} onBack={() => onStepChange('search')} onSelect={offer => {
        setSelectedFlight(offer);
        dispatch(setBookingDetails({ searchParams, passengers, customerEmail, selectedFlight: offer, search_id: offer.searchId, offer_id: offer.offerId }));
        onStepChange('passengers');
      }} /> : null}
      {currentStep === 'search' ? (
        <FlightItineraryTripSearchStep
          dateConstraints={selectedPackage?.dateConstraints}
          searchParams={searchParams}
          validationMode={validationMode}
          validationMessages={validationMessages}
          onSearchParamsChange={(params) => {
            setSelectedFlight(undefined);
            setSearchParams(params);
            dispatch(setBookingDetails({ searchParams: params, passengers, customerEmail }));
          }}
          onValidationMessagesChange={setValidationMessages}
          onNext={handleSearchNext}
          nextLabel={selectedPackage?.requiresFlightSelection ? 'Search flights' : undefined}
        />
      ) : null}

      {capacityValidationMessage ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {capacityValidationMessage}
        </Alert>
      ) : null}

      {currentStep === 'passengers' ? (
        <>
          <FlightItineraryPassengerDetailsStep
            searchParams={searchParams}
            customerEmail={customerEmail}
            passengers={passengers}
            customerEmailValidationMessages={customerEmailValidationMessages}
            passengerValidationMessages={passengerValidationMessages}
            onCustomerEmailChange={handleCustomerEmailChange}
            onPassengerChange={handlePassengerChange}
            showContinue={false}
            onEditTrip={() => {
              trackButtonClick('FlightItineraryTripDetails', 'Edit trip from passenger details');
              onStepChange('search');
            }}
          />
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={handlePassengerDetailsNext}>Next: Review details</Button>
          </Box>
        </>
      ) : null}

      {currentStep === 'review' ? (
        <>
          <FlightItineraryReviewStep
            searchParams={searchParams}
            customerEmail={customerEmail}
            passengers={passengers}
            customerEmailValidationMessages={customerEmailValidationMessages}
            passengerValidationMessages={passengerValidationMessages}
            onCustomerEmailChange={handleCustomerEmailChange}
            onPassengerChange={handlePassengerChange}
            onEditTrip={() => {
              trackButtonClick('FlightItineraryTripDetails', 'Edit trip from review');
              onStepChange('search');
            }}
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={termsAccepted}
                  onChange={(event) => {
                    setTermsAccepted(event.target.checked);
                    if (event.target.checked) setShowTermsValidationMessage(false);
                  }}
                />
              )}
              label={(
                <>
                  I agree to the{' '}
                  <Link href={`${config.app.helpCenterLink}/terms-of-service`} target="_blank" rel="noopener noreferrer" onClick={() => trackLinkClick('FlightItineraryTripDetails', 'Terms of service', `${config.app.helpCenterLink}/terms-of-service`)}>
                    terms of service
                  </Link>
                  .
                </>
              )}
            />
            {showTermsValidationMessage ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please agree to the terms of service before continuing to payment.
              </Alert>
            ) : null}
            <Box sx={{ mt: 1 }}>
              <Button variant="contained" onClick={handleReviewNext}>Next: Pay</Button>
            </Box>
          </Box>
        </>
      ) : null}
    </Box>
  );
}

export default FlightItineraryTripDetails;
